import { useState } from "react";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import MailList from "./components/MailList";
import MailDetail from "./components/MailDetail";
import ComposeModal from "./components/ComposeModal";
import CollabMailbox from "./components/CollabMailbox";
import ContactDrawer from "./components/ContactDrawer";
import AnnouncementList from "./components/AnnouncementList";
import NPCChatModal from "../../components/feature/NPCChatModal";
import { getNpcsByRole } from "../../mocks/inbox";
import { useRole } from "../../contexts/RoleContext";

export default function Inbox() {
  const { jobRole, roleConfig } = useRole();
  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [inboxTab, setInboxTab] = useState<"npc" | "collab" | "announce">("npc");
  const [openNpcContactId, setOpenNpcContactId] = useState<string | null>(null);
  const [npcChatId, setNpcChatId] = useState<string | null>(null);

  const npcs = getNpcsByRole(jobRole);

  const handleContactClick = (npcId: string) => {
    setOpenNpcContactId(prev => prev === npcId ? null : npcId);
  };

  const handleOpenNpcChat = (chatId: string) => {
    setOpenNpcContactId(null);
    setNpcChatId(chatId);
  };

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <div className="flex-1 flex overflow-hidden" style={{ height: "calc(100vh - 48px)" }}>

          {/* Mail List */}
          <div className="bg-[#111112] border-r border-white/[0.05] flex flex-col shrink-0" style={{ width: "270px" }}>
            <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
              <div>
                <span className="text-[#F2F2F2] text-sm font-medium">인박스</span>
                <p className="text-[#666666] text-[10px] mt-0.5">{roleConfig.label} 전용</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCompose(true)}
                className="flex items-center gap-1.5 bg-[#4B6CF7] hover:bg-[#5B7CF7] text-white text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-pencil-line text-sm"></i>새 메일
              </button>
            </div>

            {/* Tab switcher */}
            <div className="flex gap-1 px-3 py-2 border-b border-white/[0.05]">
              <button
                type="button"
                onClick={() => setInboxTab("npc")}
                className={`flex-1 text-[11px] py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${inboxTab === "npc" ? "bg-[#4B6CF7]/15 text-[#7A9BFF]" : "text-[#444444] hover:text-[#888888]"}`}
              >
                거래처 메일
              </button>
              <button
                type="button"
                onClick={() => setInboxTab("collab")}
                className={`flex-1 text-[11px] py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${inboxTab === "collab" ? "bg-emerald-400/15 text-emerald-400/90" : "text-[#444444] hover:text-[#888888]"}`}
              >
                협업 메일
              </button>
              <button
                type="button"
                onClick={() => setInboxTab("announce")}
                className={`flex-1 text-[11px] py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${inboxTab === "announce" ? "bg-[#FF9F43]/15 text-[#FF9F43]" : "text-[#444444] hover:text-[#888888]"}`}
              >
                공지
              </button>
            </div>

            {inboxTab === "npc" && (
              <div className="flex-1 overflow-hidden">
                <MailList selectedId={selectedMailId} onSelect={id => { setSelectedMailId(id); setOpenNpcContactId(null); }} />
              </div>
            )}
            {inboxTab === "collab" && (
              <div className="flex-1 overflow-hidden flex flex-col items-center justify-center">
                <i className="ri-team-line text-2xl text-[#555555] mb-2"></i>
                <p className="text-[#333333] text-xs px-3 text-center">오른쪽 패널에서 협업 메일을 확인하세요</p>
              </div>
            )}
            {inboxTab === "announce" && (
              <div className="flex-1 overflow-hidden flex flex-col items-center justify-center">
                <i className="ri-megaphone-line text-2xl text-[#FF9F43]/50 mb-2"></i>
                <p className="text-[#444444] text-xs px-3 text-center">오른쪽 패널에서 공지를 확인하세요</p>
              </div>
            )}
          </div>

          {/* 공지사항 탭 전체 패널 */}
          {inboxTab === "announce" && (
            <div className="flex-1 overflow-hidden">
              <AnnouncementList />
            </div>
          )}

          {/* NPC 메일 탭 */}
          {inboxTab === "npc" && (
            <>
              <div className="flex-1 bg-[#0C0C0E] flex flex-col overflow-hidden">
                <MailDetail mailId={selectedMailId} />
              </div>
              <div className="w-52 bg-[#111112] border-l border-white/[0.05] flex flex-col shrink-0 relative">
                <div className="px-4 py-3 border-b border-white/[0.05]">
                  <span className="text-[#777777] text-[10px] uppercase tracking-widest">직무 거래처</span>
                  <p className="text-[#666666] text-[9px] mt-0.5">{roleConfig.label}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                  {npcs.map((npc) => {
                    const isActive = openNpcContactId === npc.id;
                    return (
                      <button key={npc.id} type="button" onClick={() => handleContactClick(npc.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all cursor-pointer group ${isActive ? "bg-[#4B6CF7]/10 border-[#4B6CF7]/30" : "bg-[#141416] border-white/[0.05] hover:border-[#4B6CF7]/20 hover:bg-[#1A1A1E]"}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="relative shrink-0">
                            <div className={`w-7 h-7 flex items-center justify-center rounded-lg text-[10px] font-bold transition-colors ${isActive ? "bg-[#4B6CF7]/20 text-[#7A9BFF]" : "bg-white/[0.06] text-[#888888] group-hover:bg-white/[0.1]"}`}>{npc.avatar}</div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-[#111112] ${npc.isOnline ? "bg-[#4B6CF7]" : "bg-[#333333]"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-[11px] font-medium truncate transition-colors ${isActive ? "text-[#7A9BFF]" : "text-[#CCCCCC] group-hover:text-[#F2F2F2]"}`}>{npc.name}</p>
                            <p className="text-[#666666] text-[9px] truncate">{npc.isOnline ? "온라인" : "오프라인"}</p>
                          </div>
                          <i className={`ri-arrow-right-s-line text-xs transition-all ${isActive ? "text-[#4B6CF7] rotate-90" : "text-[#555555] group-hover:text-[#777777]"}`}></i>
                        </div>
                        <p className="text-[#666666] text-[9px] truncate">{npc.company}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="px-3 py-2.5 border-t border-white/[0.05]">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#777777]"><span className="text-[#4B6CF7]">{npcs.filter(n => n.isOnline).length}</span> 온라인</span>
                    <span className="text-[#666666]">{npcs.filter(n => !n.isOnline).length} 오프라인</span>
                  </div>
                </div>
                {openNpcContactId && (
                  <ContactDrawer npcId={openNpcContactId} onClose={() => setOpenNpcContactId(null)}
                    onSelectMail={id => { setSelectedMailId(id); setOpenNpcContactId(null); }}
                    onOpenNpcChat={handleOpenNpcChat} />
                )}
              </div>
            </>
          )}

          {/* 협업 메일 탭 */}
          {inboxTab === "collab" && (
            <div className="flex-1 overflow-hidden">
              <CollabMailbox />
            </div>
          )}
        </div>
      </div>

      {showCompose && <ComposeModal onClose={() => setShowCompose(false)} />}

      <NPCChatModal
        isOpen={!!npcChatId}
        onClose={() => setNpcChatId(null)}
        initialNpcId={npcChatId ?? "mentor"}
        context="거래처 협상 연습 — 실제 거래처와 유사한 AI 캐릭터와 대화하며 협상 스킬을 연습해보세요."
      />
    </div>
  );
}
